import { useEffect as useDrawerBaselineEffect } from "react";
import { useFormDirty } from "../../../../../../../shared/frontend/forms/useFormDirty";
import type { ListActionRef } from "../../../../../../../shared/frontend/list/listColumn";
import { CheckOutlined,CloseCircleOutlined,GiftOutlined } from "@ant-design/icons";

import { Button, Form, Input, Select, Tooltip } from "../../../../../../../shared/frontend/ui";
import dayjs from "dayjs";
import React from "react";
import { Link,useLocation } from "react-router-dom";
import type { LocalizedText } from "../../../app/i18n/i18n";
import { formatDateOnly } from "../../../app/units/format";
import type { UnitsSettings } from "../../../app/units/units";
import { FormDrawer } from "../../../components/FormDrawer";
import { SettingsListPage,type ColumnDefinition,type ProColumnsWithMeasure } from "../../../components/SettingsListPage";
import { makeActionColumn } from "../../../components/ui/antd";
import {
MemberPromotionGrantDto,
MemberPromotionGrantStatus,
MemberPromotionTemplateDto,
PersonOptionDto,
UpsertMemberPromotionGrantDto,
ContractPackageOptionDto,
contractPackageOptionLabel,
memberPromotionApi,
personOptionLabel
} from "../../memberPromotions/memberPromotionApi";
import { grantStatusLabel,rewardKindLabel } from "../../memberPromotions/memberPromotionLabels";
import { DatePicker } from "../../../../../../../shared/frontend/components/ControlledPickers";
import { useFeedback } from "../../../../../../../shared/frontend/feedback/useFeedback";
import { ISO_DATE_LENGTH } from "../../../app/utils/isoDate";

type Props = {
  personId: string;
  personName: string;
  canWrite: boolean;
  canExecute: boolean;
  dateInputFormat: string | string[];
  units: UnitsSettings;
  text: (value: LocalizedText) => string;
};

type FormValues = {
  templateId: string;
  currentPersonRole: "referrer" | "referred";
  otherPersonId: string;
  referredContractPackageId: string;
  referralDate: dayjs.Dayjs;
  note?: string | null;
};

export function PersonPromotionsTab({ personId, canWrite, canExecute, dateInputFormat, units, text }: Props) {
  const feedback = useFeedback();
  const loc = useLocation();
  // Herkunft mitgeben, damit die aufgerufene Personenakte hierher zurückführt statt in die Personenliste.
  const linkState = React.useMemo(() => ({ from: `${loc.pathname}${loc.search}` }), [loc.pathname, loc.search]);
  const [open, setOpen] = React.useState(false);
  const [saving, setSaving] = React.useState(false);
  const [templates, setTemplates] = React.useState<MemberPromotionTemplateDto[]>([]);
  const [personOptions, setPersonOptions] = React.useState<PersonOptionDto[]>([]);
  const [referredContractPackages, setReferredContractPackages] = React.useState<ContractPackageOptionDto[]>([]);
  const [form] = Form.useForm<FormValues>();
  const { dirty: drawerDirty, resetDirty: resetDrawerDirty } = useFormDirty(form);
  const drawerOpen = open;
  useDrawerBaselineEffect(() => { if (drawerOpen) resetDrawerDirty(); }, [drawerOpen, resetDrawerDirty]);

  const actionRef = React.useRef<ListActionRef>(null);
  const currentPersonRole = Form.useWatch("currentPersonRole", form);
  const otherPersonId = Form.useWatch("otherPersonId", form);
  const referredPersonIdForForm = currentPersonRole === "referred" ? personId : otherPersonId;
  const otherPersonLabel = currentPersonRole === "referred" ? text("Referrer") : text("Referred member");
  const formatPromotionDate = React.useCallback((value?: string | null) => {
    return value ? formatDateOnly(String(value).slice(0, ISO_DATE_LENGTH), units) : "";
  }, [units]);

  React.useEffect(() => {
    memberPromotionApi.listTemplates(false).then(setTemplates).catch(() => setTemplates([]));
  }, []);

  const searchPersons = React.useCallback((q: string) => {
    memberPromotionApi.searchPersons(q).then((rows) => {
      setPersonOptions(rows.filter((x) => x.id !== personId));
    }).catch(() => setPersonOptions([]));
  }, [personId]);

  const openCreate = () => {
    form.setFieldsValue({
      templateId: templates[0]?.id,
      currentPersonRole: "referrer",
      referredContractPackageId: undefined,
      referralDate: dayjs(),
      note: ""
    });
    setOpen(true);
  };

  React.useEffect(() => {
    if (!open || !referredPersonIdForForm) {
      setReferredContractPackages([]);
      form.setFieldValue("referredContractPackageId", undefined);
      return;
    }
    memberPromotionApi.listContractPackages(String(referredPersonIdForForm))
      .then((rows) => {
        setReferredContractPackages(rows);
        const current = form.getFieldValue("referredContractPackageId");
        if (current && !rows.some((row) => row.id === current)) {
          form.setFieldValue("referredContractPackageId", undefined);
        }
      })
      .catch(() => setReferredContractPackages([]));
  }, [form, open, referredPersonIdForForm]);

  const submit = async () => {
    const values = await form.validateFields();
    const referrerPersonId = values.currentPersonRole === "referrer" ? personId : values.otherPersonId;
    const referredPersonId = values.currentPersonRole === "referred" ? personId : values.otherPersonId;
    const body: UpsertMemberPromotionGrantDto = {
      templateId: values.templateId,
      referrerPersonId,
      referredPersonId,
      referredContractPackageId: values.referredContractPackageId,
      referralDate: values.referralDate.format("YYYY-MM-DD"),
      note: values.note || null
    };
    setSaving(true);
    try {
      await memberPromotionApi.createGrant(body);
      feedback.success(text("Created."));
      setOpen(false);
      actionRef.current?.reload?.();
    } finally {
      setSaving(false);
    }
  };

  const runAction = async (row: MemberPromotionGrantDto, fn: (id: string) => Promise<MemberPromotionGrantDto>, successKey: LocalizedText) => {
    await fn(row.id);
    feedback.success(text(successKey));
    actionRef.current?.reload?.();
  };

  const columnDefinitions: ColumnDefinition[] = [
    { key: "status", label: text("Status"), defaultVisible: true },
    { key: "templateName", label: text("Promotion template"), defaultVisible: true },
    { key: "referrerPersonName", label: text("Referrer"), defaultVisible: true },
    { key: "referredPersonName", label: text("Referred member"), defaultVisible: true },
    { key: "rewardKind", label: text("Bonus type"), defaultVisible: true },
    { key: "referralDate", label: text("Referral date"), defaultVisible: true },
    { key: "result", label: text("Result"), defaultVisible: true }
  ];

  const allColumnsMap: Record<string, ProColumnsWithMeasure<MemberPromotionGrantDto>> = React.useMemo(() => ({
    status: { title: text("Status"), dataIndex: "status", key: "status", render: (_, row) => text(grantStatusLabel(row.status)) },
    templateName: { title: text("Promotion template"), dataIndex: "templateName", key: "templateName" },
    referrerPersonName: {
      title: text("Referrer"),
      dataIndex: "referrerPersonName",
      key: "referrerPersonName",
      render: (_, row) => <Link to={`/persons/${row.referrerPersonId}`} state={linkState}>{row.referrerPersonName}</Link>
    },
    referredPersonName: {
      title: text("Referred member"),
      dataIndex: "referredPersonName",
      key: "referredPersonName",
      render: (_, row) => <Link to={`/persons/${row.referredPersonId}`} state={linkState}>{row.referredPersonName}</Link>
    },
    rewardKind: { title: text("Bonus type"), dataIndex: "rewardKind", key: "rewardKind", render: (_, row) => text(rewardKindLabel(row.rewardKind)) },
    referralDate: { title: text("Referral date"), dataIndex: "referralDate", key: "referralDate", render: (_, row) => formatPromotionDate(row.referralDate) },
    result: {
      title: text("Result"),
      key: "result",
      sortValue: (row) => (row.contractPriceAdjustmentId
        ? text("Price adjustment")
        : row.walletTransactionId
          ? text("Value account booking")
          : row.decisionReason || ""),
      render: (_, row) => {
        if (row.contractPriceAdjustmentId) return text("Price adjustment");
        if (row.walletTransactionId) return text("Value account booking");
        return row.decisionReason || "";
      }
    }
  }), [formatPromotionDate, linkState, text]);

  const actionSlots = [
    {
      show: (row: MemberPromotionGrantDto) => canExecute && (row.status === MemberPromotionGrantStatus.Pending || row.status === MemberPromotionGrantStatus.NeedsReview),
      render: (row: MemberPromotionGrantDto) => (
        <Tooltip title={text("Qualify")}>
          <Button aria-label={text("Qualify")} title={text("Qualify")} icon={<CheckOutlined />} onClick={() => runAction(row, memberPromotionApi.qualifyGrant, "Qualified.")} />
        </Tooltip>
      )
    },
    {
      show: (row: MemberPromotionGrantDto) => canExecute && row.status === MemberPromotionGrantStatus.Qualified,
      render: (row: MemberPromotionGrantDto) => (
        <Tooltip title={text("Redeem")}>
          <Button aria-label={text("Redeem")} title={text("Redeem")} icon={<GiftOutlined />} onClick={() => runAction(row, memberPromotionApi.redeemGrant, "Redeemed.")} />
        </Tooltip>
      )
    },
    {
      show: (row: MemberPromotionGrantDto) => canExecute && row.status !== MemberPromotionGrantStatus.Cancelled && row.status !== MemberPromotionGrantStatus.Redeemed,
      render: (row: MemberPromotionGrantDto) => (
        <Tooltip title={text("Cancel")}>
          <Button danger aria-label={text("Cancel")} title={text("Cancel")} icon={<CloseCircleOutlined />} onClick={() => runAction(row, (id) => memberPromotionApi.cancelGrant(id), "Cancelled.")} />
        </Tooltip>
      )
    }
  ];

  const requestGrants = React.useCallback(async () => {
    const list = await memberPromotionApi.listGrants({ personId });
    return { data: list, success: true, total: list.length };
  }, [personId]);

  return (
    <>
      <SettingsListPage<MemberPromotionGrantDto>
        storageKey="persons.detail.memberPromotions"
        rowKey="id"
        request={requestGrants}
        columnDefinitions={columnDefinitions}
        allColumnsMap={allColumnsMap}
        actionsColumn={canExecute ? makeActionColumn<MemberPromotionGrantDto>({ width: 140, slotCount: 3 }) : undefined}
        actionSlots={canExecute ? actionSlots : undefined}
        actionRef={actionRef}
        onAdd={canWrite ? openCreate : undefined}
        addButtonText="New bonus"
      />

      {!open ? <Form form={form} style={{ display: "none" }} /> : null}
      <FormDrawer dirty={drawerDirty}
        title="Create bonus"
        open={open}
        onCancel={() => setOpen(false)}
        formId="person-promotion-form"
        submitting={saving}
        submitText="Create"
      >
        <Form id="person-promotion-form" form={form} layout="vertical" onFinish={submit}>
          <Form.Item name="templateId" label={text("Promotion template")} rules={[{ required: true }]}>
            <Select showSearch optionFilterProp="label" options={templates.map((tpl) => ({ value: tpl.id, label: tpl.name }))} />
          </Form.Item>
          <Form.Item name="currentPersonRole" label={text("Referral situation")} rules={[{ required: true }]}>
            <Select options={[
              { value: "referrer", label: text("This person referred a member") },
              { value: "referred", label: text("This person was referred") }
            ]} />
          </Form.Item>
          <Form.Item name="otherPersonId" label={otherPersonLabel} rules={[{ required: true }]}>
            <Select showSearch filterOption={false} onSearch={searchPersons} options={personOptions.map((p) => ({ value: p.id, label: personOptionLabel(p) }))} />
          </Form.Item>
          <Form.Item name="referredContractPackageId" label={text("Referred contract package")} rules={[{ required: true, message: text("Please select contract package.") }]}>
            <Select
              showSearch
              optionFilterProp="label"
              disabled={!referredPersonIdForForm}
              placeholder={text("Select contract package")}
              options={referredContractPackages.map((contractPackage) => ({ value: contractPackage.id, label: contractPackageOptionLabel(contractPackage, formatPromotionDate) }))}
            />
          </Form.Item>
          <Form.Item name="referralDate" label={text("Referral date")} rules={[{ required: true }]}>
            <DatePicker format={dateInputFormat} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item name="note" label={text("Internal note")}>
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </FormDrawer>
    </>
  );
}
